import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { orderApi } from '@formosa/shared/api/order';
import { ApiError } from '@formosa/shared/api/request';
import SettingsSubPageShell from '../../components/settings/SettingsSubPageShell';
import { useTranslation } from '../../i18n/LocaleContext';
import { formatFollowPeriodLabel, filterFollowOrdersBySymbol } from '../../utils/followOrder';

export default function FollowOrderListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const symbolParam = searchParams.get('symbol');
  const { t } = useTranslation();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const normalizedSymbol = useMemo(
    () => (symbolParam ? String(symbolParam).trim().toUpperCase() : ''),
    [symbolParam]
  );

  const loadRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const page = await orderApi.listFollowOrders({ pageNo: 1, pageSize: 50 });
      setRecords(filterFollowOrdersBySymbol(page?.records || [], normalizedSymbol));
    } catch (err) {
      setRecords([]);
      setError(err instanceof ApiError ? err.message : t('follow.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [normalizedSymbol, t]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  function openConfirm(item) {
    const base = `/trade/futures/follow/${item.id}`;
    const path = symbolParam ? `${base}?symbol=${encodeURIComponent(symbolParam)}` : base;
    navigate(path, { state: { followOrder: item } });
  }

  return (
    <SettingsSubPageShell title={t('follow.title')}>
      <div className="follow-list-page">
        {loading && <p className="follow-list-page__status">{t('common.loading')}</p>}
        {error && <p className="follow-list-page__error">{error}</p>}

        {!loading && records.length === 0 && !error && (
          <p className="follow-list-page__status">{t('common.noData')}</p>
        )}

        <ul className="follow-list-page__list">
          {records.map((item) => (
            <li key={item.id}>
              <button type="button" className="follow-list-page__item" onClick={() => openConfirm(item)}>
                <div>
                  <div className="follow-list-page__name">{item.name || t('common.dash')}</div>
                  <div className="follow-list-page__meta">
                    {t('follow.period')}：{formatFollowPeriodLabel(item.periodType)}
                  </div>
                </div>
                <span className="follow-list-page__chevron" aria-hidden>
                  ›
                </span>
              </button>
            </li>
          ))}
        </ul>

        {!loading && records.length > 0 && (
          <p className="follow-list-page__footer">{t('follow.noMore')}</p>
        )}
      </div>
    </SettingsSubPageShell>
  );
}
